/**
 * 旅行心愿的数据来源（TRV-06）：页面、攻略列表、地图那一行都从这里拿，不各自去找。
 * - 当前活动心愿：GET /travel/wish（合同 §23.4，已可接 live）。fixture 与 live 都走 visits 服务的 travelWish（journey 模块提供，
 *   fixture 实现按需加载演示数据）。服务在查询函数里才取：服务不论以哪种方式失败（没接入、报错、根本不在），
 *   都只落到这条查询的错误态——地图面板与列表照常，只是没有心愿那一行（照 world_map/schoolNote 的做法）。
 * - 计划（第③期，2026-09-24 23:2x 接上）：live 走 visits 服务的 travelPlan（GET /travel/plans/{plan_id}，I 已实现，能力表 travel.plan 可用），
 *   与心愿同一口径——只在拿到当前宠物以后才读、显式带 pet_id；演示仍是 fixture 的演示计划（动态 import，单独一个 chunk，主包里没有演示数据），
 *   每份演示计划各带自己的心愿（PlanBundle）。计划页 = 这份计划（手账在它当前那一版的 journals 里）+ GET /travel/wish 的心愿
 *   （业务状态 status、TA 的理由 owner_reason；只有 wish_id 对得上才放在一起，对不上就只按计划本身说，见 ./model 的 viewOfPlanOnly）。
 *   别人家的、不存在的、还没发布过计划的心愿都是 404 + plan_not_found（见 isPlanNotFound）；后端没有这条路（路由级 404）不算这一种。
 * - 惦记（2026-09-27，合同 TRV-00 §38.3）：GET /travel/longing，fixture 与 live 都走 visits 服务的 travelLonging（fixture 按需加载演示数据）。
 *   惦记另占一格：和当前心愿是两条查询、两个键，谁也不挡谁（四种组合见 ./longing 的 wishAndLonging）。
 */
import { useQuery } from "@tanstack/react-query";
import type { TravelLonging, TravelPlan, TravelWish } from "@/shared/contracts";
import { isApiError, toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { travelScopeKey, useTravelScope, type CurrentTravel, type TravelScope } from "./currentScope";
import { journalPollInterval, type PlanBundle } from "./model";

/** 读的几种状态。出错时 retry 只在值得再试时给（后端说明不可重试的，如 404，不给重试键）。 */
export type Read<T> = { state: "unavailable" } | { state: "loading" } | { state: "error"; error: unknown; retry?: () => void } | { state: "ready"; data: T };

/**
 * 当前宠物的活动心愿；没有活动心愿时 data 为 null（I 已定：GET /travel/wish 此时返回 200 + null）。
 * 一律显式传当前宠物的 id：pet_id 省略（null）时后端按 require_pet 去推，只有能照顾的宠物恰好一只才默认它，否则 409 pet_required（§29.5）——
 * 所以 live 只在拿到当前宠物以后才读、从不传 null；演示实现不看 petId（演示世界的家庭上下文没有当前宠物，不发请求）。
 */
export function useCurrentWish(): Read<TravelWish | null> {
  return useCurrentTravel("wish");
}
/** Same original service and fixture provenance; live never uses an old user/pet-only cache. */
export function useCurrentLonging(): Read<TravelLonging | null> {
  return useCurrentTravel("longing");
}
function useCurrentTravel<K extends "wish" | "longing">(kind: K): Read<(K extends "wish" ? TravelWish : TravelLonging) | null> {
  type Value = (K extends "wish" ? TravelWish : TravelLonging) | null;
  const services = useServices(), authority = useTravelScope(), fixture = env.dataMode === "fixture";
  const scope = authority.scope;
  const get = (petId: string | null, signal: AbortSignal) => (kind === "wish" ? services.visits.travelWish(petId, signal) : services.visits.travelLonging(petId, signal)) as Promise<Value>;
  const query = useQuery<CurrentTravel<Value> | Value>({
    queryKey: fixture ? ["travel", kind, "demo"] : currentTravelKey(kind, scope),
    queryFn: ({ signal }) => fixture ? get(null, signal) : authority.read(signal, () => get(scope!.petId, signal)),
    enabled: fixture || Boolean(scope),
    ...(fixture ? {} : { retry: false, staleTime: 0, refetchOnMount: true, refetchOnWindowFocus: false, refetchOnReconnect: false }),
  });
  if (fixture) {
    if (query.isPending) return { state: "loading" };
    if (query.isError) return { state: "error", error: query.error };
    return { state: "ready", data: query.data as Value };
  }
  return currentRead(authority, query, () => void query.refetch());
}
export function currentTravelKey(kind: "wish" | "longing", scope?: TravelScope) {
  return ["travel", kind, scope?.userId ?? "-", scope?.petId ?? "-", "current-scope", scope?.householdId ?? "-", scope?.homeId ?? "-", scope?.householdVersion ?? "-"] as const;
}
function currentRead<T>(authority: ReturnType<typeof useTravelScope>, query: { isPending: boolean; isFetching: boolean; isError: boolean; error: unknown; data?: CurrentTravel<T> | T }, retry: () => void): Read<T> {
  if (!authority.canRead) return { state: "unavailable" };
  if (authority.error) return { state: "error", error: authority.error, retry: () => void authority.refresh() };
  if (!authority.scope || !authority.current() || query.isPending) return { state: "loading" };
  if (query.isError) return { state: "error", error: query.error, retry: isApiError(query.error) && !query.error.retryable ? undefined : retry };
  const data = query.data as CurrentTravel<T> | undefined;
  if (!data?.scope || travelScopeKey(data.scope) !== travelScopeKey(authority.scope) || !(data.confirmed >= authority.confirmed)) return { state: "loading" };
  return { state: "ready", data: data.value };
}

function reasonOf(error: unknown): string | null {
  const reason = toApiError(error).details?.reason;
  return typeof reason === "string" ? reason : null;
}

/**
 * GET /travel/plans/{plan_id} 答“这份计划还没写好，或者已经不在了”：404 + details.reason = plan_not_found（形状同 no_journey）。
 * 后端没有这条路的路由级 404 不带 details，不算这一种（照常走统一错误态）。
 */
export function isPlanNotFound(error: unknown): boolean {
  return toApiError(error).status === 404 && reasonOf(error) === "plan_not_found";
}

/** live 某一份计划的查询键（读计划与“重画”共用：重画成功后把返回的计划直接写回这一条，冲突时让这一条重读）。 */
export function planQueryKey(userId: string | null, petId: string | null, planId: string | undefined, scope?: TravelScope) {
  // Three-argument legacy callers retain the old prefix, never a live fallback.
  const base = ["travel", "plan", userId ?? "-", petId ?? "-", planId ?? "-"] as const;
  return scope ? [...base, "current-scope", scope.householdId, scope.homeId, scope.householdVersion] as const : base;
}

/**
 * live 的某一份计划（GET /travel/plans/{plan_id}）。与 useCurrentWish 同一口径：只在拿到当前宠物以后才读、显式带 pet_id（不拿 null 让后端去推）；
 * 键带账号、宠物与计划号（切宠物就是另一条）。演示模式不走这里（state 恒为 unavailable），演示计划见 useDemoPlans。
 * 页面上那一页手账图在画时，每 10 秒只读重取一次，画好（或没画成、没确认）就停、离开页面就停（./model 的 journalPollInterval，2026-09-26 巡检 R14-A05）。
 * poll: false——不显示手账图的地方（攻略页卡片、心愿页只拿回忆手账说“这一趟的结果”，DS5）只读一次、不跟着手账图轮询；键不变，缓存照样共用。
 */
export function useLivePlan(planId: string | undefined, { poll = true }: { poll?: boolean } = {}): Read<TravelPlan> {
  const services = useServices(), authority = useTravelScope(), scope = authority.scope;
  const enabled = env.dataMode === "live" && Boolean(scope && planId);
  const query = useQuery<CurrentTravel<TravelPlan>>({
    queryKey: planQueryKey(scope?.userId ?? null, scope?.petId ?? null, planId, scope),
    queryFn: ({ signal }) => authority.read(signal, () => services.visits.travelPlan(planId!, scope!.petId, signal), planId),
    enabled, retry: false, staleTime: 0, refetchOnMount: true, refetchOnWindowFocus: false, refetchOnReconnect: false,
    refetchInterval: enabled && poll ? (q) => journalPollInterval(q.state.data?.value) : false,
  });
  if (env.dataMode !== "live" || !planId) return { state: "unavailable" };
  return currentRead(authority, query, () => void query.refetch());
}

/** 旅行路由没法确定是哪只宠物：409 + details.reason = pet_required（require_pet 的惯例，§29.5）。页面说“需要先选一只宠物”，不白屏、不一直转。 */
export function isPetRequired(error: unknown): boolean {
  return toApiError(error).status === 409 && reasonOf(error) === "pet_required";
}

/** 演示计划（只在 fixture；每份各带自己的心愿）；live 的计划见 useLivePlan。 */
export function useDemoPlans(): Read<readonly PlanBundle[]> {
  const fixture = env.dataMode === "fixture";
  const query = useQuery({
    queryKey: ["travel", "plans", "demo"],
    queryFn: async () => (await import("./fixture")).DEMO_PLANS,
    enabled: fixture,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  if (!fixture) return { state: "unavailable" };
  if (query.isPending) return { state: "loading" };
  if (query.isError) return { state: "error", error: query.error };
  return { state: "ready", data: query.data };
}
